import { memo } from 'react';
import { Box, Tooltip, Stack, Typography } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';

interface ScoreAnswerProps {
    score: string;
    displayFormula?: string | null;
    prefilledFormula?: string | null;
}

/**
 * Displays a formula question score with an optional tooltip showing the calculation breakdown.
 * If no formula is provided, displays only the score.
 *
 * @param {ScoreAnswerProps} props
 * @returns {JSX.Element}
 */
const ScoreAnswer: React.FC<ScoreAnswerProps> = ({ score, displayFormula = null, prefilledFormula = null }) => {
    const scoreElement = (
        <Box component="span" sx={{ fontWeight: 'medium' }}>
            {score}
        </Box>
    );

    if (!displayFormula) {
        return <Box sx={{ mt: 0.25 }}>{scoreElement}</Box>;
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, mt: 0.25 }}>
            {scoreElement}
            <Tooltip
                title={
                    <Stack spacing={0.5}>
                        <Typography variant="h5">How was this score calculated?</Typography>
                        {[
                            { label: 'Formula:', value: displayFormula },
                            ...(prefilledFormula ? [{ label: 'With values:', value: `${prefilledFormula}=${score}` }] : []),
                        ].map(({ label, value }, index) => (
                            <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, minWidth: '90px', flexShrink: 0 }}>
                                    {label}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: 'monospace',
                                        color: 'text.secondary',
                                        wordBreak: 'break-all',
                                        overflowWrap: 'anywhere',
                                    }}
                                >
                                    {value}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                }
                arrow
                placement="right"
                slotProps={{
                    tooltip: {
                        sx: {
                            bgcolor: 'common.white',
                            color: 'text.primary',
                            boxShadow: 3,
                            border: '1px solid',
                            borderColor: 'grey.300',
                            borderRadius: 1,
                            p: 1.5,
                            maxWidth: 500,
                            '& .MuiTooltip-arrow': {
                                color: 'common.white',
                                '&::before': {
                                    border: '1px solid',
                                    borderColor: 'grey.300',
                                },
                            },
                        },
                    },
                }}
            >
                <InfoOutlined
                    sx={{
                        fontSize: '16px',
                        color: 'secondary.main',
                        cursor: 'help',
                        '&:hover': {
                            color: 'primary.dark',
                        },
                    }}
                />
            </Tooltip>
        </Box>
    );
};

export default memo(ScoreAnswer);
